'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { ChevronDown, Check, Plus } from 'lucide-react';
import { useUserRole } from '@/hooks/auth/useUserRole';
import { useWorkspaceSwitcher } from '@/hooks/useWorkspaceSwitcher';
import type { Workspace } from '@/services/workspace/workspaceService';
import { cn } from '../../utils';
import { CreateWorkspaceModal } from './create-workspace-modal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from './dropdown-menu';

const TRIGGER_CLASS = cn(
  'flex items-center gap-1.5 h-9 px-2 rounded-md',
  'bg-transparent hover:bg-surface-hover',
  'transition-colors duration-200',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
  'min-w-0',
);

const Divider = () => (
  <span className="text-border mx-2 text-lg font-light select-none">/</span>
);

interface AccountGroup {
  account: Workspace['account'];
  items: Workspace[];
}

export const WorkspaceSwitcher: React.FC = () => {
  const { isAdmin } = useUserRole();
  const { workspaces, currentWorkspace, isLoading, switchWorkspace, currentUserId } = useWorkspaceSwitcher();
  // The account a create-workspace modal is open for (null = closed).
  const [modalAccount, setModalAccount] = useState<{ id: string; name: string; isHoldingPen: boolean } | null>(null);

  const currentAccount = currentWorkspace?.account;
  const accountName = currentAccount?.name || 'My Account';

  // Group every workspace the user can see by its owning account. Holding-pen
  // account floats to the top, then alphabetical.
  const groupedWorkspaces = useMemo<AccountGroup[]>(() => {
    const groups = new Map<string, AccountGroup>();
    for (const ws of workspaces) {
      const accId = String(ws.account_id ?? 'unknown');
      if (!groups.has(accId)) {
        groups.set(accId, { account: ws.account, items: [] });
      }
      groups.get(accId)!.items.push(ws);
    }
    return Array.from(groups.values()).sort((a, b) => {
      // The pen floats to the top, identified by the FLAG the backend already
      // carries. It used to match the slug `knitknot` — which the account split
      // hands to the REAL KnitKnot company while renaming the pen `holding-pen`,
      // so the literal would have floated exactly the wrong account and made two
      // identically-named workspaces impossible to tell apart from the trigger.
      const aHold = a.account?.is_holding_pen ? 0 : 1;
      const bHold = b.account?.is_holding_pen ? 0 : 1;
      if (aHold !== bHold) return aHold - bHold;
      return (a.account?.name || '').localeCompare(b.account?.name || '');
    });
  }, [workspaces]);

  // The account the active workspace belongs to, and its sibling workspaces —
  // the ONLY workspaces the right-hand dropdown lists.
  const currentAccountId = String(currentWorkspace?.account_id ?? currentAccount?.id ?? 'unknown');
  const currentAccountGroup = groupedWorkspaces.find(
    (g) => String(g.account?.id ?? 'unknown') === currentAccountId,
  );
  const accountWorkspaces = currentAccountGroup?.items ?? (currentWorkspace ? [currentWorkspace] : []);

  const isMultiAccount = groupedWorkspaces.length > 1;
  const showAccountDropdown = isMultiAccount;
  const showWorkspaceDropdown = accountWorkspaces.length > 1 || isAdmin;

  const handleSwitch = useCallback((ws: Workspace) => {
    switchWorkspace(ws);
  }, [switchWorkspace]);

  // Open the create modal for a given account. Deferred a tick so the closing
  // dropdown menu fully unmounts before the dialog grabs focus (Radix race).
  const openCreateModal = useCallback((accountId: string, accName: string, isHoldingPen: boolean) => {
    setTimeout(() => setModalAccount({ id: accountId, name: accName, isHoldingPen }), 0);
  }, []);

  const workspaceLabel = currentWorkspace?.name || (isLoading ? '' : 'Workspace');

  // An account is usually created named after the company, and its first
  // workspace takes the same name — so the top bar spent two of its slots
  // saying one word: "Intryc ⌄ / Intryc". Drop the workspace segment when it
  // would repeat the account label AND has no dropdown of its own, i.e. when
  // it is a static span with no behaviour to lose. Nothing becomes
  // unreachable: the account dropdown already expands each account into its
  // workspaces, and this branch only fires when the account has ONE workspace
  // and the viewer can't create another. A workspace whose name diverges
  // ("Intryc EU") still renders, which is exactly when the word earns its
  // slot.
  const workspaceRepeatsAccount =
    !showWorkspaceDropdown &&
    !!workspaceLabel &&
    workspaceLabel.trim().toLowerCase() === accountName.trim().toLowerCase();

  // Clone the new workspace's member list from a workspace in the SAME account
  // (the active one if it lives there, else the account's first workspace).
  const modalSourceId = useMemo(() => {
    if (!modalAccount) return undefined;
    const group = groupedWorkspaces.find((g) => String(g.account?.id ?? 'unknown') === modalAccount.id);
    return group?.items.find((w) => w.id === currentWorkspace?.id)?.id ?? group?.items[0]?.id;
  }, [modalAccount, groupedWorkspaces, currentWorkspace?.id]);

  return (
    <div className="flex items-center gap-0 min-w-0">
      <Divider />

      {/* Account switcher (left) */}
      {showAccountDropdown ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={TRIGGER_CLASS}>
              <span className="text-sm font-medium text-ink-content truncate max-w-[140px]">
                {accountName}
              </span>
              <ChevronDown className="h-3.5 w-3.5 text-ink-supporting shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-52">
            {/* Each account expands (on hover) into its workspaces, so you can
                jump straight to any workspace in any account — and create a new
                one there — without switching accounts first. */}
            {groupedWorkspaces.map((group) => {
              const accId = String(group.account?.id ?? 'unknown');
              const isCurrent = accId === currentAccountId;
              return (
                <DropdownMenuSub key={accId}>
                  <DropdownMenuSubTrigger className="cursor-pointer">
                    {/*
                      * `account == null` is not a category — it is the backend's
                      * documented DEGRADED state (`with_account_summaries` logs
                      * "degrading to account=None" when the summary fetch fails or
                      * the reference dangles), and `AccountSummary.name` is
                      * non-optional whenever the account IS present. So no account
                      * is ever actually NAMED the placeholder that used to sit
                      * here: the label asserted a name we simply failed to load.
                      * Render nothing (rule 21) — the submenu still lists this
                      * group's workspaces by name, which is the real identifier.
                      */}
                    <span className="flex-1 truncate">{group.account?.name}</span>
                    {isCurrent && (
                      <span className="ml-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                    )}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-52">
                    <div className="max-h-64 overflow-y-auto">
                      {group.items.map((ws) => (
                        <DropdownMenuItem
                          key={ws.id}
                          onClick={() => handleSwitch(ws)}
                          className="flex cursor-pointer items-center gap-1.5"
                        >
                          <span className="min-w-0 truncate">{ws.name}</span>
                          {ws.id === currentWorkspace?.id && (
                            <Check className="ml-auto h-3.5 w-3.5 shrink-0 text-primary" />
                          )}
                        </DropdownMenuItem>
                      ))}
                    </div>
                    {isAdmin && (
                      <CreateWorkspaceRow
                        accountId={accId}
                        accountName={group.account?.name ?? "this account"}
                        isHoldingPen={group.account?.is_holding_pen === true}
                        onOpen={openCreateModal}
                      />
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <span className="text-sm font-medium text-ink-content truncate max-w-[140px] px-2 h-9 inline-flex items-center">
          {accountName}
        </span>
      )}

      {!workspaceRepeatsAccount && <Divider />}

      {/* Workspace switcher (right) — scoped to the current account */}
      {workspaceRepeatsAccount ? null : showWorkspaceDropdown ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className={TRIGGER_CLASS}>
              <span className="text-sm font-medium text-ink-content truncate max-w-[140px]">
                {workspaceLabel}
              </span>
              <ChevronDown className="h-3.5 w-3.5 text-ink-supporting shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-52">
            {isLoading ? (
              <div className="px-2 py-1.5 text-xs text-ink-supporting">Loading...</div>
            ) : (
              <div className="max-h-64 overflow-y-auto">
                {accountWorkspaces.map((ws) => (
                  <DropdownMenuItem
                    key={ws.id}
                    onClick={() => handleSwitch(ws)}
                    className="flex cursor-pointer items-center gap-1.5"
                  >
                    <span className="min-w-0 truncate">{ws.name}</span>
                    {ws.id === currentWorkspace?.id && (
                      <Check className="ml-auto h-3.5 w-3.5 shrink-0 text-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
              </div>
            )}
            {isAdmin && (
              <CreateWorkspaceRow
                accountId={currentAccountId}
                accountName={accountName}
                isHoldingPen={currentAccount?.is_holding_pen === true}
                onOpen={openCreateModal}
              />
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <span className="text-sm font-medium text-ink-content truncate max-w-[140px] px-2 h-9 inline-flex items-center">
          {workspaceLabel}
        </span>
      )}

      {modalAccount && (
        <CreateWorkspaceModal
          open
          onOpenChange={(o) => { if (!o) setModalAccount(null); }}
          accountId={modalAccount.id}
          accountName={modalAccount.name}
          isHoldingPen={modalAccount.isHoldingPen}
          userId={currentUserId}
          sourceWorkspaceId={modalSourceId}
          onCreated={(ws) => { setModalAccount(null); switchWorkspace(ws, { to: '/onboarding' }); }}
        />
      )}
    </div>
  );
};

function CreateWorkspaceRow({
  accountId,
  accountName,
  isHoldingPen,
  onOpen,
}: {
  accountId: string;
  accountName: string;
  isHoldingPen: boolean;
  onOpen: (accountId: string, accountName: string, isHoldingPen: boolean) => void;
}) {
  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        onSelect={() => onOpen(accountId, accountName, isHoldingPen)}
        className="cursor-pointer text-ink-supporting"
      >
        <Plus className="mr-2 h-3.5 w-3.5 shrink-0" />
        <span className="min-w-0 truncate">New workspace in {accountName}</span>
      </DropdownMenuItem>
    </>
  );
}
